import { Spinner } from "@bestowinc/kindred-ds";
import type {
  ErrorModuleComponent,
  FlowMeta,
  ModuleRef,
  TinkertoyErrorInfo,
} from "@bestowinc/tinkertoy-core";
import { useEffect, useRef, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import { Helmet } from "react-helmet";
import { isRouteErrorResponse, useRouteError } from "react-router";

import {
  FAILURE_EVENT_TAGS,
  isModuleLoadError,
  markFailureReported,
  reportFailureOnce,
  reportShellBoundaryFailure,
  resolveErrorCode,
  resolveFailureError,
  resolveFailureMessage,
} from "../error-telemetry";
import type { LoadedModule } from "../module-loader";
import { useModuleLoader } from "../module-loader/module-loader-context";
import { DeveloperToolsHost } from "../developer-tools/DeveloperToolsHost";
import { readSerializedShellConfig } from "../runtime-config/read-shell-config";
import { ShellLoaderError } from "./shell-loader-error";

export type { TinkertoyErrorInfo };

export function ShellErrorBoundary() {
  const error = useRouteError();
  const {
    status,
    message,
    code,
    errorView,
    errorContext,
    flowMeta,
    errorModuleData,
    trace,
  } = narrowError(error);

  const reportedRef = useRef(false);
  useEffect(() => {
    if (reportedRef.current) {
      return;
    }
    reportedRef.current = true;
    reportShellBoundaryFailure(error, {
      tag: FAILURE_EVENT_TAGS.shellBoundary,
      errorCode: resolveErrorCode(error),
      message: resolveFailureMessage(error),
      moduleId: errorContext?.moduleId,
      traceId: trace?.traceId,
    });
  }, [error, errorContext?.moduleId, trace?.traceId]);

  return (
    <>
      <ShellDocumentTitle flowMeta={flowMeta} />
      {errorView ? (
        <ErrorViewHost
          errorView={errorView}
          status={status}
          message={message}
          code={code}
          errorType={errorContext?.errorType ?? code}
          moduleId={errorContext?.moduleId}
          errorModuleData={errorModuleData}
          trace={trace}
        />
      ) : (
        <InlineFallback status={status} message={message} trace={trace} />
      )}
      <DeveloperToolsHost />
    </>
  );
}

function ShellDocumentTitle({ flowMeta }: { flowMeta?: FlowMeta }) {
  const title =
    flowMeta?.title ?? readSerializedShellConfig()?.defaultDocumentTitle;
  if (!title) {
    return null;
  }

  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
}

type ErrorTrace = Pick<ShellLoaderError, "traceId" | "traceIdSource" | "stackTrace">;

interface NarrowedError {
  status: number;
  message: string;
  code: string;
  errorView?: ModuleRef;
  errorContext?: { errorType: string; moduleId?: string };
  flowMeta?: FlowMeta;
  errorModuleData?: unknown;
  /**
   * Only {@link ShellLoaderError} carries one — and only it can carry an
   * `errorView`, so every rendered error page has a trace ID.
   */
  trace?: ErrorTrace;
}

function narrowError(error: unknown): NarrowedError {
  if (ShellLoaderError.isInstance(error)) {
    return {
      status: error.status,
      message: error.message,
      code: error.code,
      errorView: error.errorView,
      errorContext: error.errorContext,
      flowMeta: error.flowMeta,
      errorModuleData: error.errorModuleData,
      trace: {
        traceId: error.traceId,
        traceIdSource: error.traceIdSource,
        ...(error.stackTrace ? { stackTrace: error.stackTrace } : {}),
      },
    };
  }
  if (isRouteErrorResponse(error)) {
    return {
      status: error.status,
      message:
        typeof error.data === "string"
          ? error.data
          : (error.statusText ?? "An unexpected error occurred."),
      code: "route_error",
    };
  }
  if (isModuleLoadError(error)) {
    return {
      status: 500,
      message: resolveFailureMessage(error),
      code: error.code,
      errorContext: {
        errorType: "module_load_failure",
        moduleId: error.moduleId,
      },
    };
  }
  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message,
      code: "unknown",
    };
  }
  return {
    status: 500,
    message: "An unexpected error occurred.",
    code: "unknown",
  };
}

interface ErrorViewHostProps {
  errorView: ModuleRef;
  status: number;
  message: string;
  code: string;
  errorType: string;
  moduleId?: string;
  errorModuleData?: unknown;
  trace?: ErrorTrace;
}

function ErrorViewHost({
  errorView,
  status,
  message,
  code,
  errorType,
  moduleId,
  errorModuleData,
  trace,
}: ErrorViewHostProps) {
  const loadState = useErrorViewModule(errorView);

  if (loadState.kind === "loading") {
    return <ErrorViewLoading />;
  }

  if (loadState.kind === "error") {
    return <InlineFallback status={status} message={message} trace={trace} />;
  }

  const ErrorComponent =
    loadState.module.definition.component as ErrorModuleComponent<
      Record<string, unknown>,
      unknown
    >;

  const error: TinkertoyErrorInfo = {
    type: errorType,
    code,
    status,
    message,
    ...(moduleId ? { moduleId } : {}),
    ...trace,
  };

  return (
    <ErrorComponent
      props={errorView.props}
      error={error}
      data={errorModuleData ?? {}}
    />
  );
}

type ModuleLoadState =
  | { kind: "loading" }
  | { kind: "loaded"; module: LoadedModule }
  | { kind: "error"; error: unknown };

function useErrorViewModule(errorView: ModuleRef): ModuleLoadState {
  const loader = useModuleLoader();
  const [state, setState] = useState<ModuleLoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    loader
      .loadModule(errorView.moduleId, errorView.minVersion, {
        allowErrorModule: true,
      })
      .then((module) => {
        if (!cancelled) {
          setState({ kind: "loaded", module });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          if (isModuleLoadError(err)) {
            const failureError = resolveFailureError(
              err,
              `Failed to load error view module "${errorView.moduleId}".`,
            );
            reportFailureOnce(failureError, {
              tag: FAILURE_EVENT_TAGS.errorViewLoad,
              errorCode: err.code,
              message: failureError.message,
              error: failureError,
              moduleId: err.moduleId,
              moduleVersion: err.version,
            });
            markFailureReported(err);
          }
          setState({ kind: "error", error: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loader, errorView.moduleId, errorView.minVersion]);

  return state;
}

/** Not found and expired-link errors aren't support cases, so they get no reference. */
const STATUSES_WITHOUT_REFERENCE: ReadonlySet<number> = new Set([404, 410]);

function InlineFallback({
  status,
  message,
  trace,
}: {
  status: number;
  message: string;
  trace?: ErrorTrace;
}) {
  const showReference =
    trace !== undefined && !STATUSES_WITHOUT_REFERENCE.has(status);

  return (
    <div data-testid="shell-error-boundary" role="alert">
      <h1>{status}</h1>
      <p>{message}</p>
      {showReference ? (
        <p data-testid="shell-error-boundary-reference">
          {trace.traceIdSource === "newrelic" ? "Trace ID" : "Reference"}:{" "}
          {trace.traceId}
        </p>
      ) : null}
      {trace?.stackTrace ? (
        <pre data-testid="shell-error-boundary-stack-trace">
          {trace.stackTrace}
        </pre>
      ) : null}
    </div>
  );
}

function ErrorViewLoading() {
  return (
    <div
      data-testid="shell-error-boundary-loading"
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <Spinner size="large" />
    </div>
  );
}
